/* eslint-disable no-restricted-properties */
/* eslint-disable prefer-exponentiation-operator */
import {mix, Eventable} from '../mixin';
import frameTimer from '../service/frameTimer';

const {Math} = globalThis;

const easeLinear = x => x;

class Tween extends mix().with(Eventable)
{
    #target;
    #keys;
    #fromValues;
    #toValues;
    #getters = {};
    #setters = {};
    #lastTime;
    #duration = 0;
    #timerId = 0;
    #progress = 0;
    #easeFn;

    /** Stellt die wichtigsten Easing-Funktionen zur Verfügung
     * @static
     * @type {Object}
     * @memberof Tween
     * @see https://easings.net
     */
    static ease = Object.freeze({
        inSine: x => 1 - Math.cos((x * Math.PI) / 2),
        outSine: x => Math.sin((x * Math.PI) / 2),
        inOutSine: x => -(Math.cos(Math.PI * x) - 1) / 2,
        inQuad: x => x * x,
        outQuad: x => 1 - (1 - x) * (1 - x),
        InOutQuad: x => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2),
        inCubic: x => x * x * x,
        outCubic: x => 1 - Math.pow(1 - x, 3),
        InOutCubic: x => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2)
    });

    /** Der Tween startet automatisch.
     * @mixes Eventable
     * @param {Object} target Das Objekt, dessen Werte getweent werden sollen
     * @param {number} duration Dauer des Tweens
     * @param {Object} toValues Die Zielwerte
     * @param {Function=} easeFn Eine Easing-Funktion
     * @fires Tween#event:update
     * @fires Tween#event:completed
     */
    constructor(target, duration, toValues, easeFn = easeLinear)
    {
        super();
        this.#target = target;
        this.#keys = Object.keys(toValues);
        this.#duration = duration * 1000;
        this.#createGettersAndSetters();
        this.#fromValues = this.#getValues();
        this.#toValues = toValues;
        this.#easeFn = easeFn;
        this.play();
    }

    /** Der Fortschritt zwischen ``0`` und ``1`` lässt sich jederzeit auslesen und setzen.
     * @type {number}
     */
    get progress()
    {
        return this.#progress;
    }

    set progress(progress)
    {
        this.#progress = progress;
        if (!this.#timerId)
        {
            this.#setValues();
        }
    }

    #getValues()
    {
        const values = {};
        for (const key of this.#keys)
        {
            values[key] = this.#getters[key]();
        }
        return values;
    }

    #createGettersAndSetters()
    {
        const keys = this.#keys;
        const target = this.#target;
        for (const key of keys)
        {
            if (typeof target[key] === 'function')
            {
                this.#getters[key] = () => parseFloat(target[key]());
                this.#setters[key] = value => target[key](value);
            }
            else
            {
                this.#getters[key] = () => parseFloat(target[key] || 0);
                this.#setters[key] = (value) => { target[key] = value; };
            }
        }
    }

    /**
     * @return {Tween}
     */
    play()
    {
        if (!this.#timerId && this.#target)
        {
            this.#lastTime = Date.now();
            this.#timerId = frameTimer.every(1, time => this.#advance(time));
        }
        return this;
    }

    /**
     * @return {Tween}
     */
    pause()
    {
        if (this.#timerId && this.#target)
        {
            frameTimer.cancel(this.#timerId);
            this.#timerId = null;
        }
        return this;
    }

    /**
     * @return {Tween}
     */
    restart()
    {
        this.pause();
        this.progress = 0;
        this.play();
    }

    /**
     * @return {Tween}
     */
    destroy()
    {
        this.pause();
        this.#target = null;
        this.#keys = null;
        this.#fromValues = null;
        this.#toValues = null;
        this.#getters = null;
        this.#setters = null;
    }

    #advance(time)
    {
        this.#progress += (time - this.#lastTime) / this.#duration;
        if (this.#progress >= 1)
        {
            this.#end();
        }
        else
        {
            this.#setValues();
            /**
             * @event Tween#update
             * @property {Object} target
             */
            this.fire('update', {target: this.#target});
        }
        this.#lastTime = Date.now();
    }

    #setValues()
    {
        const progress = this.#easeFn(this.#progress);
        const fromValues = this.#fromValues;
        const toValues = this.#toValues;

        for (const key of this.#keys)
        {
            const value = fromValues[key] + (progress * (toValues[key] - fromValues[key]));
            this.#setters[key](value);
        }
    }

    #end()
    {
        this.pause();
        for (const key of this.#keys)
        {
            this.#setters[key](this.#toValues[key]);
        }
        this.fire('update', {target: this.#target});
        /**
         * @event Tween#completed
         * @property {Object} target
         */
        this.fire('completed', {target: this.#target});
    }
}

export default Tween;
